/* Executive-minimalist tokens (shared by /report/* screens).
   Values come from the single source of truth in ./tokens.js — do not hardcode. */
import { COLORS, RADIUS, FONT, SELECT_STYLES } from './tokens';

/* SELECT_STYLES is re-exported so existing `import { SELECT_STYLES } from '.../txStyles'`
   keeps working and renders identically to <PCSelect> (bordered white). */
export { SELECT_STYLES };

export const CARD = {
    background: COLORS.surface,
    borderRadius: RADIUS.card,
    boxShadow: 'none',
    border: `1px solid ${COLORS.border}`
};

export const COL_HEAD = {
    fontWeight: 600,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: COLORS.textDim,
    padding: '12px 16px',
    background: COLORS.surfaceAlt,
    borderBottom: `1px solid ${COLORS.border}`,
    whiteSpace: 'nowrap'
};

export const INPUT = {
    background: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: RADIUS.input,
    padding: '10px 14px',
    fontSize: 14,
    fontFamily: FONT,
    width: '100%',
    boxSizing: 'border-box'
};

/* ── Entry boxes that never cut their value (user, 2026-10-28) ─────────────────────────────────
   In a table cell an input is only as wide as its column, so with the 14px inner padding a
   narrow column showed "380.0" or "Rent Charg". These keep every value whole:
     AMOUNT_INPUT  wide enough for "000,000.00"
     DATE_INPUT    wide enough for "dd/mm/yyyy" plus the calendar icon
     QTY_INPUT     a small number (quantity / in-row amount editor)
     DESC_INPUT    free text: a text area that wraps at its spaces (words stay whole) and grows with
                   its text — use <Form.Control as="textarea" rows={1} className={DESC_CLASS}
                   style={DESC_INPUT} ref={fitDescription} onKeyDown={keepOneLine} …/>
     WRAP_SELECT   react-select `styles` whose chosen value wraps instead of ending in "…"  */
export const AMOUNT_INPUT = { ...INPUT, minWidth: 'calc(10ch + 30px)' };
export const DATE_INPUT = { ...INPUT, minWidth: 'calc(10ch + 54px)' };
export const QTY_INPUT = { minWidth: 'calc(8ch + 24px)' };
export const DESC_CLASS = 'pc-desc-input';
export const DESC_INPUT = {
    ...INPUT, minWidth: '22ch', resize: 'none', overflow: 'hidden', lineHeight: 1.4,
    whiteSpace: 'pre-wrap', overflowWrap: 'normal', wordBreak: 'normal', fieldSizing: 'content',
};
// `field-sizing: content` grows the box where supported; elsewhere fitDescription() sets its height.
const GROWS_ITSELF = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('field-sizing', 'content');
export function fitDescription(el) {
    if (!el || GROWS_ITSELF) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;   // + the 1px borders (border-box)
}
// A description is one line of data: Enter must not add a line break to the saved text.
export const keepOneLine = (e) => { if (e.key === 'Enter') e.preventDefault(); };
// Re-wrap every description box when the window changes width (browsers without field-sizing).
if (typeof window !== 'undefined' && !GROWS_ITSELF) {
    window.addEventListener('resize', () => document.querySelectorAll(`textarea.${DESC_CLASS}`).forEach(fitDescription));
}
export const WRAP_SELECT = {
    // keeps the app's default value colour (tokens.js SELECT_STYLES), which these styles replace key-by-key
    singleValue: (base) => ({ ...base, color: COLORS.text, whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', overflowWrap: 'normal' }),
    // the minimum goes on the whole dropdown (value + its arrow), so the arrow never drops to a
    // second line when the column is narrow
    container: (base) => ({ ...base, minWidth: 'calc(16ch + 56px)' }),
};

export const LABEL = {
    fontWeight: 500,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: COLORS.textDim,
    display: 'block',
    marginBottom: 6
};

export function ToggleSwitch({ options, value, onChange, disabled }) {
    return (
        <div style={{ display: 'flex', background: COLORS.surfaceAlt, padding: 3, borderRadius: RADIUS.card, border: `1px solid ${COLORS.border}` }}>
            {options.map(opt => (
                <button
                    key={opt}
                    disabled={disabled}
                    onClick={() => !disabled && onChange(opt)}
                    style={{
                        flex: 1,
                        padding: '6px 16px',
                        borderRadius: 6,
                        border: 'none',
                        fontWeight: 600,
                        fontSize: 12,
                        background: value === opt ? COLORS.surface : 'transparent',
                        color: value === opt ? COLORS.primary : COLORS.textDim,
                        boxShadow: value === opt ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.6 : 1,
                        whiteSpace: 'nowrap',
                        transition: 'all 0.15s'
                    }}
                >
                    {opt.toUpperCase()}
                </button>
            ))}
        </div>
    );
}
